import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoggerService } from '@app/services/logger.service';
import { getEnvironment } from '@app/shared/utils';
import { environment } from '@environments/environment';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { displayMessage, ICatalogInfo, LoginErrorCode } from '@ra-state';
import { ActionButtonStyles, IDialogConfig, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { DataService } from '@services/data.service';
import { HarnessService } from '@services/harness.service';
import { map, Observable, switchMap, tap } from 'rxjs';
import { CommandRequestBuilderService } from 'src/app/services/command-request-builder.service';
import { openWebTechDialog } from '../modal';
import {
  AppInitFinished,
  getProductCatalog,
  logoutSession,
  permissionDenied,
  setProductCatalogSuccess,
  startAppInitializer,
  userLoginComplete,
  userLoginFailed,
} from './control-page.actions';

@Injectable({
  providedIn: 'root',
})
export class ControlPageEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly harnessService = inject(HarnessService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  initializeApp$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(startAppInitializer),
      tap(() => {
        this.harnessService.initializeTarget({
          identifier: 'common-admin-portal',
          attributes: {
            Type: 'Application',
          },
        });
      }),
      switchMap(() => {
        return [AppInitFinished()];
      }),
    );
  });

  ngrxOnInitEffects(): Action {
    this.logger.log('Initializing effects');
    return startAppInitializer();
  }

  loginCompleted$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => {
        return [getProductCatalog()];
      }),
    );
  });

  getProductCatalog$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getProductCatalog),
      switchMap(() => {
        return this.dataService.getCatalogInfo$();
      }),
      switchMap((catalog: ICatalogInfo[]) => {
        return [setProductCatalogSuccess({ payload: catalog })];
      }),
    );
  });

  missingRolesErrorDialogConfig(env): IDialogConfig {
    return {
      title: 'Your account is not authorized',
      message: `Please request access via IIQ for FTHub Admin portal role(s) for ${env} environment `,
      buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Outlined }],
    };
  }

  defaultErrorConfig: IDialogConfig = {
    title: 'Unable to sign you in',
    message:
      'We ran into an unexpected issue while signing you in. Please create a support ticket and we will help resolve the issue. ',
    buttons: [
      { label: 'Create ticket', buttonStyle: ActionButtonStyles.Main },
      { label: 'Close', buttonStyle: ActionButtonStyles.Outlined },
    ],
  };

  loginFailed$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginFailed),
      map(({ payload: loginError }) => {
        if (loginError.errorCode === LoginErrorCode.AdminPortalRoleNotFound) {
          const env = getEnvironment(environment.appConfiguration.csPortalUrl);
          return openWebTechDialog({
            config: this.missingRolesErrorDialogConfig(env),
            notificationType: NotificationType.Error,
            closeHandler: logoutSession(),
          });
        }
        return openWebTechDialog({
          config: this.defaultErrorConfig,
          notificationType: NotificationType.Error,
          closeHandler: logoutSession(),
        });
      }),
    );
  });

  permissionDenied$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(permissionDenied),
      tap((permissionDenied) => {
        if (permissionDenied.handleRedirectOnDenied) {
          permissionDenied.handleRedirectOnDenied();
        }
      }),
      switchMap((permissionDeniedPayload) => {
        const actions: Action[] = [];
        actions.push(
          displayMessage({
            payload: {
              message: permissionDeniedPayload.errorMessage,
              type: 'Error',
            },
          }),
        );
        return actions;
      }),
    );
  });
}
